import React, { useEffect, useState } from 'react'
import { isEmpty, map } from 'lodash'

import {
  Box,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { Controller, useForm } from 'react-hook-form'
import MultiSelect from '@kenshooui/react-multi-select'

import { BaseDialog, Label, LoadingFeedback } from 'components'

import useCompanyFragility from 'hooks/useCompanyFragility'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'

import useStyles from './styles'
import schema from './schema'

const QuestionControlsDialog = ({
  open,
  setOpen,
  categories,
  defaultSelected,
  refresh,
}) => {
  const classes = useStyles()

  const {
    companyFragility,
    setMeasuresSelected,
    questionControls,
    setQuestionControls,
  } = useCompanyFragility()
  const snackbar = useSnackbar()

  const [selectedCategories, setSelectedCategories] = useState([])
  const [selection, setSelection] = useState(defaultSelected)
  const [questionControlOptions, setQuestionControlOptions] =
    useState(defaultSelected)
  const [loading, setLoading] = useState(false)

  const multiSelectSearch = (value) => (item) => {
    return (
      String(item.label).toLowerCase().includes(value.toLowerCase()) ||
      String(item.group).toLowerCase().includes(value.toLowerCase())
    )
  }

  const changeCategories = (_, value) => {
    setSelectedCategories(value)
  }

  const { handleSubmit, control, errors, watch } = useForm({
    validationSchema: schema,
    defaultValues: {
      title: '',
      subtitle: '',
      formKind: '',
      status: null,
      radioValue: 'Select',
    },
  })

  const radioValue = watch('radioValue')

  const loadQuestionControlOptions = async (categoryIds) => {
    if (!isEmpty(selectedCategories)) {
      const response = await service.dponet.questionControls.list({
        questionnaireKind: categoryIds,
        perPage: 9999,
        status: [
          constants.questionControl.ADOPTED_STATUS,
          constants.questionControl.RISK_ASSUMED_STATUS,
          constants.questionControl.TREATED_STATUS,
          constants.questionControl.PENDING_STATUS,
          constants.questionControl.IN_PROGRESS_STATUS,
          constants.questionControl.OVERDUE_STATUS,
          constants.questionControl.WAITING_ANSWER_STATUS,
        ],
      })
      return setQuestionControlOptions(
        helpers.questionControls.dataMultiOptions(
          response?.data?.questionControls,
        ),
      )
    }
    setQuestionControlOptions(selection)
  }

  useEffect(() => {
    loadQuestionControlOptions(map(selectedCategories, 'id'))
    //eslint-disable-next-line
  }, [selectedCategories])

  const handleSave = async () => {
    try {
      if (!!companyFragility?.id) {
        const data = helpers.questionControls.manageOptions(
          questionControls,
          selection,
        )

        const response =
          await service.dponet.companyFragilities.manageQuestionControl({
            companyFragilityId: companyFragility.id,
            ...data,
          })

        setQuestionControls(response?.data?.companyFragilityMeasures ?? [])
      }

      snackbar.open({
        variant: 'success',
        message: 'Medidas se segurança atualizadas com sucesso!',
      })

      setMeasuresSelected(selection)
      setOpen(false)
    } catch (error) {
      snackbar.open({
        variant: 'error',
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
      })
    }
  }

  const onSubmit = async (data) => {
    try {
      setLoading(true)
      data.status = data?.status?.id

      await service.dponet.questionControls.create(data)

      snackbar.open({
        variant: 'success',
        message: 'Medida de Segurança criada com sucesso!',
      })
      refresh()
      setOpen(false)
    } catch (error) {
      snackbar.open({
        variant: 'error',
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error,
          false,
        ),
      })
    } finally {
      setLoading(false)
    }
  }

  const onClick = (e) => {
    if (radioValue === 'Create') {
      return handleSubmit(onSubmit)(e)
    }
    handleSave()
  }

  return (
    <BaseDialog
      actionButton={onClick}
      open={open}
      setOpen={setOpen}
      title={
        radioValue === 'Create'
          ? 'Criar medida de segurança'
          : companyFragility?.id
          ? 'Editar medidas de segurança'
          : 'Adicionar medida de segurança'
      }
      closeButtonText="Voltar"
      actionButtonText="Salvar"
      dialogSize="lg"
      fullWidth
    >
      <LoadingFeedback open={loading} />
      <Box>
        <Controller
          as={
            <FormControl>
              <FormLabel>Selecione ou crie uma medida de segurança</FormLabel>
              <RadioGroup value={radioValue}>
                <FormControlLabel
                  value="Select"
                  control={<Radio />}
                  label="Selecionar medida de segurança"
                />
                <FormControlLabel
                  value="Create"
                  control={<Radio />}
                  label="Criar medida de segurança"
                />
              </RadioGroup>
            </FormControl>
          }
          mode="onChange"
          control={control}
          name="radioValue"
        />
      </Box>
      {radioValue === 'Select' && (
        <>
          <Grid container spacing={2}>
            <Label
              titleClassName={classes.labelTitle}
              titleVariant="subtitle1"
              title="Selecione a categoria"
              item
              xs={12}
            >
              <Autocomplete
                getOptionLabel={(value) => value.name}
                options={categories}
                onChange={changeCategories}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
                renderOption={(option) => (
                  <Box className={classes.categoriesText}>{option.name}</Box>
                )}
                multiple
              />
            </Label>

            <Label
              titleClassName={classes.labelTitle}
              titleVariant="subtitle1"
              title="Medidas de segurança"
              item
              xs={12}
            >
              <MultiSelect
                messages={helpers.dataCollected.messagesMultiSelect()}
                items={questionControlOptions}
                withGrouping
                selectedItems={defaultSelected}
                onChange={(selected) => {
                  setSelection(selected)

                  return selected
                }}
                filterFunction={multiSelectSearch}
                wrapperClassName={classes.multiSelect}
              />
            </Label>
          </Grid>
        </>
      )}
      {radioValue === 'Create' && (
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                as={
                  <TextField
                    error={!!errors.title}
                    helperText={errors?.title?.message}
                    label="Medida"
                    fullWidth
                  />
                }
                mode="onChange"
                control={control}
                name="title"
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                as={
                  <TextField
                    error={!!errors.formKind}
                    helperText={errors?.formKind?.message}
                    label="Criar nova categoria"
                    variant="outlined"
                    fullWidth
                  />
                }
                mode="onChange"
                control={control}
                name="formKind"
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                as={
                  <TextField
                    minRows={3}
                    multiline
                    label="Descrição"
                    fullWidth
                    name="subtitle"
                  />
                }
                mode="onChange"
                control={control}
                name="subtitle"
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                as={
                  <Autocomplete
                    options={constants.questionControl.STATUSES.filter(
                      (status) => ['adopted', 'pending'].includes(status.id),
                    )}
                    getOptionLabel={(option) => option?.name || ''}
                    getOptionSelected={(option, value) =>
                      option?.id === value?.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!errors.status}
                        helperText={errors?.status?.message}
                        label="Status"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                }
                onChange={([, data]) => data}
                mode="onChange"
                control={control}
                name="status"
              />
            </Grid>
          </Grid>
        </form>
      )}
    </BaseDialog>
  )
}

export default QuestionControlsDialog
